import { SVG_ENUM } from "enums";

import { downloadFile, getCurrencyFromUuid, formatNumber } from "utils";
import { TransactionType } from "types/TransactionsTypes";
import moment from "moment";
import { CompanyMyType } from "types/CompanyTypes";
import { SVG, ContextMenu } from "shared";
import { SharesByCompanyType } from "types/SharesTypes";

const items = ({
  item,
  company,
  setEditTransaction,
  isAdmin,
  isOwn,
}: {
  item: TransactionType;
  company: CompanyMyType;
  setEditTransaction?: (company: SharesByCompanyType) => void;
  isAdmin: boolean | undefined;
  isOwn: boolean;
}) => {
  return (item: any) => {
    const actions = [];
    const canEdit =
      !!setEditTransaction &&
      ((company as any).origin === "other" || item.type === "sell" || isOwn) &&
      !isAdmin;

    if (canEdit) {
      actions.push({
        name: "Edytuj",
        action: () =>
          setEditTransaction!({
            ...item,
            name: company?.name || "",
            availableShares: company?.availableShares,
            isStock: company?.type === "stock",
            editTransaction: true,
            companyUuid: company?.uuid,
          }),
        svg: SVG_ENUM.EDIT,
      });
    }

    if (item?.file) {
      actions.push({
        name: "Pobierz",
        action: () =>
          downloadFile({ name: item.file.name, url: item.file.url }),
        svg: SVG_ENUM.FILE,
      });
    }
    return actions;
  };
};

export const mobileColumns = (
  company: CompanyMyType,
  setEditTransaction: ((company: SharesByCompanyType) => void) | undefined,
  isAdmin: boolean | undefined,
  isOwn: boolean = false,
) => [
  {
    width: "calc(100% - 20px)",
    render: (data: TransactionType | any) => {
      if (data?.type === "transformation") {
        return {
          children: (
            <div className="company-details-transformation company-details-transformation--mobile">
              <div className="company-details-transformation__title">
                <span className="company-details-transformation__icon">
                  <SVG type={SVG_ENUM.WARNING_CLOUD} />
                </span>
                <div className="company-details-transformation__title-wrap">
                  <div className="company-details-transformation__date">
                    {data?.transformedAt
                      ? moment(data.transformedAt).format("DD/MM/YYYY")
                      : data?.completedAt
                      ? moment(data.completedAt).format("DD/MM/YYYY")
                      : ""}
                  </div>
                  Zmiana rodzaju spółki na spółkę akcyjną
                </div>
              </div>
              <div className="company-details-transformation__row">
                <span className="company-details-transformation__label">
                  Parytet wymiany
                </span>
                <span className="company-details-transformation__value">
                  {data?.exchangeRatio || "-"}
                </span>
              </div>
              <div className="company-details-transformation__row">
                <span className="company-details-transformation__label">
                  Ilość udziałów
                </span>
                <span className="company-details-transformation__value">
                  {data?.sharesBeforeTransformation ?? "-"}
                </span>
                <span className="company-details-transformation__arrow">
                  <SVG type={SVG_ENUM.CHEVRON} />
                </span>
                <span className="company-details-transformation__label company-details-transformation__label--count">
                  Ilość akcji
                </span>
                <span className="company-details-transformation__value">
                  {data?.sharesAfterTransformation ?? "-"}
                </span>
              </div>
            </div>
          ),
          props: { colSpan: 2, className: "rc-table-cell--white" },
        } as any;
      }

      return (
        <table className="side-modal__table">
          <tr>
            <td className="side-modal__table-label">Transakcja:</td>
            <td className="side-modal__table-cell">
              {data.type === "buy" ? "Zakup" : "Sprzedaż"}
            </td>
          </tr>
          <tr>
            <td className="side-modal__table-label">Data:</td>
            <td className="side-modal__table-cell">
              {moment(data.createdAt).format("DD/MM/YYYY")}
            </td>
          </tr>
          <tr>
            <td className="side-modal__table-label">Ilość sztuk:</td>
            <td className="side-modal__table-cell">
              {
                <span className={`transactions-table__number `}>
                  {data?.type === "buy" ? "+" : "-"}
                  {formatNumber(data?.sharesCount)}
                </span>
              }
            </td>
          </tr>
          <tr>
            <td className="side-modal__table-label">Koszt nabycia:</td>
            <td className="side-modal__table-cell">
              {
                <span className={`transactions-table__number`}>
                  {formatNumber(data?.sharesValue)}{" "}
                  {getCurrencyFromUuid(company?.currencyUuid)}
                </span>
              }
            </td>
          </tr>
          <tr>
            <td className="side-modal__table-label">Cena nabycia:</td>
            <td className="side-modal__table-cell">
              {data?.pricePerShare ? formatNumber(data?.pricePerShare) : "-"}{" "}
              {data?.pricePerShare && company
                ? getCurrencyFromUuid(company?.currencyUuid)
                : null}
            </td>
          </tr>
        </table>
      );
    },
  },

  {
    width: 88,
    render: (item: any) => {
      if (item?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }

      return (
        <>
          {item?.file ? (
            <span
              className="clients-table__invoice"
              onClick={() =>
                downloadFile({ name: item.file.name, url: item.file.url })
              }
            >
              <SVG type={SVG_ENUM.FILE} />
            </span>
          ) : null}
          {setEditTransaction &&
          (company.isNonReliancePurchase ||
            (company as any).origin === "other" ||
            item.type === "sell" ||
            isOwn) &&
          !isAdmin ? (
            <span
              className="clients-table__invoice"
              onClick={() =>
                setEditTransaction({
                  ...item,
                  name: company?.name || "",
                  availableShares: company?.availableShares,
                  editTransaction: true,
                })
              }
            >
              <SVG type={SVG_ENUM.EDIT} />
            </span>
          ) : null}
        </>
      );
    },
  },
];

export const desktopColumns = (
  company: CompanyMyType,
  setEditTransaction: ((company: SharesByCompanyType) => void) | undefined,
  isAdmin: boolean | undefined,
  isOwn: boolean = false,
) => [
  {
    title: "Transakcja",
    width: 150,
    render: (item: any) => {
      if (item?.type === "transformation") {
        return {
          children: (
            <div className="company-details-transformation">
              <div className="company-details-transformation__title">
                <span className="company-details-transformation__icon">
                  <SVG type={SVG_ENUM.WARNING_CLOUD} />
                </span>
                <div className="company-details-transformation__title-wrap">
                  <div className="company-details-transformation__date">
                    {item?.transformedAt
                      ? moment(item.transformedAt).format("DD/MM/YYYY")
                      : item?.completedAt
                      ? moment(item.completedAt).format("DD/MM/YYYY")
                      : ""}
                  </div>
                  Zmiana rodzaju spółki na spółkę akcyjną
                </div>
              </div>
              <div className="company-details-transformation__row">
                <span className="company-details-transformation__label">
                  Parytet wymiany
                </span>
                <span className="company-details-transformation__value">
                  {item?.exchangeRatio || "-"}
                </span>
              </div>
              <div className="company-details-transformation__row">
                {(() => {
                  return (
                    <>
                      <span className="company-details-transformation__label">
                        Ilość udziałów
                      </span>
                      <span className="company-details-transformation__value">
                        {item.sharesBeforeTransformation ?? "-"}
                      </span>
                      <span className="company-details-transformation__arrow">
                        <SVG type={SVG_ENUM.CHEVRON} />
                      </span>
                      <span className="company-details-transformation__label company-details-transformation__label--count">
                        Ilość akcji
                      </span>
                      <span className="company-details-transformation__value">
                        {item.sharesAfterTransformation ?? "-"}
                      </span>
                    </>
                  );
                })()}
              </div>
            </div>
          ),
          props: { colSpan: 7, className: "rc-table-cell--white" },
        } as any;
      }

      return (
        <span className="clients-table__name">
          {item?.type === "buy" ? "Zakup" : "Sprzedaż"}
        </span>
      );
    },
  },
  {
    title: "Data",
    width: 150,
    dataIndex: "completedAt",
    key: "completedAt",
    render: (item: string, row: any) => {
      if (row?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }
      return moment(item).format("DD/MM/YYYY");
    },
  },
  {
    title: "Ilość sztuk",
    width: 140,
    render: (item: TransactionType) => {
      if ((item as any)?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }
      return (
        <span className={`transactions-table__number`}>
          {item?.type === "buy" ? "+" : "-"}
          {formatNumber(item?.sharesCount)}
        </span>
      );
    },
  },
  {
    title: "Umowa",
    width: 100,
    render: (item: TransactionType, row: any) => {
      if (row?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }

      if (!setEditTransaction && item.file?.url) {
        return (
          <span
            className="clients-table__invoice"
            onClick={() =>
              downloadFile({ name: item.file.name, url: item.file.url })
            }
          >
            <SVG type={SVG_ENUM.FILE} />
          </span>
        );
      }

      return (
        <>
          {item.fileId ? (
            <SVG
              className="transactions-table__files-check"
              type={SVG_ENUM.FILES_CHECK}
            />
          ) : (
            "Brak"
          )}
        </>
      );
    },
  },
  {
    title: "Koszt nabycia",
    width: 100,
    render: (item: TransactionType, row: any) => {
      if (row?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }

      return item?.sharesValue ? (
        <span className={`transactions-table__number `}>
          {formatNumber(item?.sharesValue)}{" "}
          {getCurrencyFromUuid(company?.currencyUuid)}
        </span>
      ) : (
        "-"
      );
    },
  },
  {
    title: "Cena nabycia",
    width: 100,
    render: (item: TransactionType, row: any) => {
      if (row?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }

      return item?.sharesValue ? (
        <span className={`transactions-table__number `}>
          {formatNumber(item?.pricePerShare)}{" "}
          {getCurrencyFromUuid(company?.currencyUuid)}
        </span>
      ) : (
        "-"
      );
    },
  },
  {
    width: 40,
    render: (item: TransactionType | any) => {
      if (item?.type === "transformation") {
        return { children: null, props: { colSpan: 0 } } as any;
      }

      if (!setEditTransaction) return null;

      const canEdit =
        company.isNonReliancePurchase ||
        (company as any).origin === "other" ||
        (item.type === "sell" && !isAdmin) ||
        isOwn;
      const canShowDots = canEdit || item?.file;

      if (!canShowDots) {
        return null;
      }

      return (
        <ContextMenu
          data={item}
          items={items({
            item,
            company,
            setEditTransaction,
            isAdmin,
            isOwn,
          })(item)}
        >
          <SVG type={SVG_ENUM.DOTS} />
        </ContextMenu>
      );
    },
  },
];

export const summaryColumns = (item: any) => [
  {
    key: "type",
    width: 285,
    render: () => {
      return "Stan obecny";
    },
  },
  {
    width: 200,
    render: (item: CompanyMyType) =>
      item?.sharesCount || (item.shares && item.shares[0]?.sharesCount),
  },
  {
    width: 190,
  },

  {
    width: 30,
  },
  {
    width: 40,
  },
];
